import { memo, ReactNode } from 'react'
import { Box, ButtonBase, Stack, Typography } from '@mui/material'
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined'
import MailIcon from '../../assets/icons/MailIcon'
import PhoneIcon from '../../assets/icons/PhoneIcon'
import FeedbackIcon from '../../assets/icons/FeedbackIcon'
import SupportHeader from './SupportHeader'
import SupportScrollArea from './SupportScrollArea'
import SupportRow from './SupportRow'
import SectionLabel from './SectionLabel'
import {
    COMPANY_NAME,
    formatIndianMobile,
    initialsOf,
    REGISTERED_OFFICE,
    SUPPORT_EMAIL,
    SUPPORT_TEAM,
    SupportContactPerson,
    telHref,
    WEBSITE_URL
} from './supportContacts'

type Props = {
    onBack: () => void
    /** Switches to the in-app feedback composer. */
    onWriteToUs: () => void
}

/** Small inline link with a leading icon: phone or email on a person card. */
const ContactLink = ({ icon, label, href, ariaLabel }: { icon: ReactNode; label: string; href: string; ariaLabel: string }) => (
    <ButtonBase
        component="a"
        href={href}
        aria-label={ariaLabel}
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 0.75,
            py: 0.375,
            ml: -0.75,
            borderRadius: 1,
            fontSize: 12.5,
            lineHeight: 1.4,
            color: 'text.secondary',
            fontFamily: 'inherit',
            textDecoration: 'none',
            transition: 'color 0.15s, background-color 0.15s',
            '& svg': { fontSize: 16, flexShrink: 0 },
            '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
            '&.Mui-focusVisible': { bgcolor: 'action.hover' }
        }}
    >
        {icon}
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {label}
        </Box>
    </ButtonBase>
)

const PersonCard = ({ person, last }: { person: SupportContactPerson; last: boolean }) => (
    <Stack direction="row" spacing={1.25} sx={{ py: 1.5, borderBottom: last ? 0 : 1, borderColor: 'divider' }}>
        <Box
            aria-hidden
            sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'action.hover',
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: '0.02em',
                flexShrink: 0,
                mt: 0.25
            }}
        >
            {initialsOf(person.name)}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" fontWeight={500} noWrap>
                {person.name}
            </Typography>
            <Typography variant="caption" color="text.tertiary" sx={{ display: 'block', lineHeight: 1.4 }}>
                {person.role}
            </Typography>
            <Stack sx={{ mt: 0.75, alignItems: 'flex-start' }}>
                <ContactLink
                    icon={<PhoneIcon />}
                    label={formatIndianMobile(person.phone)}
                    href={telHref(person.phone)}
                    ariaLabel={`Call ${person.name}`}
                />
                <ContactLink icon={<MailIcon />} label={person.email} href={`mailto:${person.email}`} ariaLabel={`Email ${person.name}`} />
            </Stack>
        </Box>
    </Stack>
)

const ContactView = ({ onBack, onWriteToUs }: Props) => (
    <>
        <SupportHeader title="Contact us" onBack={onBack} />
        <SupportScrollArea>
            <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.55 }}>
                    We're a small team and we read everything. Pick whichever is easiest.
                </Typography>
                <Stack spacing={0.75}>
                    <SupportRow
                        icon={<FeedbackIcon />}
                        title="Write to us here"
                        description="Stay in the app, add screenshots"
                        onClick={onWriteToUs}
                        data-event="Contact Channel Clicked"
                        data-event-intent="in-app"
                    />
                    <SupportRow
                        icon={<MailIcon />}
                        title="Email support"
                        description={SUPPORT_EMAIL}
                        href={`mailto:${SUPPORT_EMAIL}`}
                        data-event="Contact Channel Clicked"
                        data-event-intent="email"
                    />
                    <SupportRow
                        icon={<LanguageOutlinedIcon />}
                        title="Visit tick2trade.com"
                        description="Product site and legal pages"
                        href={WEBSITE_URL}
                        data-event="Contact Channel Clicked"
                        data-event-intent="website"
                    />
                </Stack>

                <Box sx={{ mt: 3.5 }}>
                    <SectionLabel sx={{ mb: 0 }}>Team</SectionLabel>
                    {SUPPORT_TEAM.map((person, i) => (
                        <PersonCard key={person.email} person={person} last={i === SUPPORT_TEAM.length - 1} />
                    ))}
                </Box>

                <Box sx={{ mt: 3 }}>
                    <SectionLabel>Registered office</SectionLabel>
                    <Typography variant="body2" fontWeight={500}>
                        {COMPANY_NAME}
                    </Typography>
                    {REGISTERED_OFFICE.map((line) => (
                        <Typography key={line} variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
                            {line}
                        </Typography>
                    ))}
                </Box>
            </Box>
        </SupportScrollArea>
    </>
)

export default memo(ContactView)
